import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { apiClient } from "@/lib/api";

// -----------------------------------------------------------------
// AuthContext — global auth state for CDI.
//
// Every network call now goes through the shared `apiClient` so the
// Bearer-token request interceptor AND the fixed 401 handler stay
// consistent with the rest of the app (AdminDashboard, lib/api
// callers, etc). This file no longer touches raw `axios` or its
// global defaults — a single instance means a single 401 policy.
//
// State shape:
//   loading  : true while /api/auth/session is being resolved on mount
//   user     : { id, email, verified } | null
//   token    : Bearer JWT (kept in localStorage as a cookie fallback
//              for browsers that reject third-party httpOnly cookies)
// -----------------------------------------------------------------

const TOKEN_KEY = "cdi.auth.token";

// Set to `true` the first time any auth flow (session probe, login,
// magic-link consume, signup-magic consume, verify-email) resolves
// with a real user, and cleared on explicit logout. Stored on
// `window` so the module-scope 401 interceptor in `lib/api.js` can
// read it without pulling in React context. The /auth/session-expired
// redirect in the global 401 handler ONLY fires when this flag is
// true — a 401 before the first successful auth is a signed-out
// cold-load, not an expired session.
const _markAuthenticated = () => {
  try {
    if (typeof window !== "undefined") {
      window.__cdiWasAuthenticated = true;
    }
  } catch { /* noop */ }
};
const _clearAuthenticatedFlag = () => {
  try {
    if (typeof window !== "undefined") {
      window.__cdiWasAuthenticated = false;
    }
  } catch { /* noop */ }
};

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  const _setToken = useCallback((tok) => {
    try {
      if (tok) localStorage.setItem(TOKEN_KEY, tok);
      else localStorage.removeItem(TOKEN_KEY);
    } catch { /* private mode */ }
  }, []);

  const refresh = useCallback(async () => {
    // Cold-load / signed-out visitors hit this on every mount. A 401
    // here is NOT an "expired session" — it's the normal "no user"
    // state, so we silently clear `user` and let RequireAuth route
    // the visitor to /auth/login. The lib/api.js 401 interceptor
    // short-circuits for the `/auth/` prefix so this call does not
    // trigger a /auth/session-expired redirect.
    try {
      const r = await apiClient.get("/auth/session");
      const u = r.data?.user || null;
      setUser(u);
      if (u) _markAuthenticated();
      return u;
    } catch {
      // Cold-load 401 — not-logged-in state. Leave the
      // `wasAuthenticated` flag untouched; it stays false so the
      // global 401 interceptor skips the session-expired redirect
      // for every subsequent 401 until the user actually signs in.
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    // Embedded-preview session seeding: if the parent page opens the
    // preview iframe with `?token=…`, adopt it into localStorage and
    // strip it from the URL immediately so it doesn't linger in browser
    // history or get shared accidentally.
    if (typeof window !== "undefined") {
      try {
        const params = new URLSearchParams(window.location.search);
        const seeded = params.get("token");
        if (seeded) {
          localStorage.setItem(TOKEN_KEY, seeded);
          params.delete("token");
          const cleanQs = params.toString();
          const cleanUrl = `${window.location.pathname}${cleanQs ? `?${cleanQs}` : ""}${window.location.hash || ""}`;
          window.history.replaceState({}, "", cleanUrl);
        }
      } catch { /* noop — best-effort seed, existing flow still works */ }
    }
    (async () => {
      await refresh();
      setLoading(false);
    })();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const r = await apiClient.post("/auth/login", { email, password });
    const u = r.data?.user || null;
    if (!u) {
      // Treat a 200-without-user response as a hard failure so the UI
      // never redirects into the authenticated shell before the user
      // state is actually set. Falls into Login.jsx's catch block
      // like any other login error.
      throw new Error("Login response missing user");
    }
    _setToken(r.data?.access_token);
    // Order matters: mark the window flag and user state BEFORE
    // returning so the caller's post-await code (Login.jsx) sees a
    // confirmed-authenticated world before firing navigate("/"). The
    // `_markAuthenticated` call is unconditional here — we only reach
    // this line on a verified-user response, so there is no code path
    // that can redirect without having flipped the auth marker first.
    setUser(u);
    _markAuthenticated();
    return u;
  }, [_setToken]);

  const signup = useCallback(async (email, password) => {
    const r = await apiClient.post("/auth/signup", { email, password });
    // Signup no longer auto-logs the user in — email verification is
    // required before the first sign-in. Do NOT populate token / user
    // state here; Signup.jsx routes the user to /auth/login with a
    // "please verify" notice on success.
    return r.data;
  }, []);

  const signupMagic = useCallback(async (email) => {
    const r = await apiClient.post("/auth/signup-magic", { email });
    return r.data;
  }, []);

  const magicLink = useCallback(async (email) => {
    const r = await apiClient.post("/auth/magic-link", { email });
    return r.data;
  }, []);

  const consumeMagicLink = useCallback(async (token) => {
    const r = await apiClient.post("/auth/magic-link/consume", { token });
    _setToken(r.data?.access_token);
    const u = r.data?.user || null;
    setUser(u);
    if (u) _markAuthenticated();
    return u;
  }, [_setToken]);

  const verifyEmail = useCallback(async (token) => {
    const r = await apiClient.post("/auth/verify-email", { token });
    // No auto-login — the verify-email endpoint no longer issues a
    // session. Caller (VerifyEmail.jsx) renders a "Go to Login" CTA.
    return r.data;
  }, []);

  const resendVerification = useCallback(async (email) => {
    const r = await apiClient.post("/auth/resend-verification", { email });
    return r.data;
  }, []);

  const resetRequest = useCallback(async (email) => {
    const r = await apiClient.post("/auth/reset-request", { email });
    return r.data;
  }, []);

  const resetPassword = useCallback(async (token, password) => {
    const r = await apiClient.post("/auth/reset", { token, password });
    return r.data;
  }, []);

  const selectPlan = useCallback(async (basicOwned, premiumPlan) => {
    const r = await apiClient.post("/auth/select-plan", {
      basic_owned: basicOwned,
      premium_plan: premiumPlan,
    });
    setUser((prev) => prev ? {
      ...prev,
      basic_owned: r.data.basic_owned,
      premium_plan: r.data.premium_plan,
    } : prev);
    return r.data;
  }, []);

  const logout = useCallback(async () => {
    // Explicit logout is the ONLY path that clears the cached token;
    // the global 401 handler no longer touches localStorage so a
    // transient 401 can't log the user out from under themselves.
    try { await apiClient.post("/auth/logout"); } catch { /* silent */ }
    _setToken(null);
    _clearAuthenticatedFlag();
    setUser(null);
  }, [_setToken]);

  const value = {
    loading, user,
    login, signup, signupMagic, logout,
    magicLink, consumeMagicLink,
    verifyEmail, resendVerification, resetRequest, resetPassword,
    selectPlan,
    refresh,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}

// Phase 7 TBAC — read-only tier lens over the current session. Backend
// stamps `tier` and `is_admin` on every /session response so these values
// stay in sync with a live subscription change on the next refresh().
export function useTier() {
  const { user } = useAuth();
  const tier      = user?.tier || (user ? "trial" : "trial_expired");
  const isAdmin   = !!user?.is_admin;
  const isPremium = isAdmin || tier === "premium";
  const isBasic   = isAdmin || tier === "premium" || tier === "basic";
  const isTrial   = tier === "trial";
  const isExpired = tier === "trial_expired";
  return {
    tier,
    isAdmin,
    isPremium,
    isBasicOrAbove: isBasic,
    isTrial,
    isExpired,
    // Feature capabilities. Every export/import/OCR surface is
    // Premium-only per the CDI Master Rule — Basic buys the app but
    // does NOT unlock the exports/imports/invoice parser. Admin bypass
    // is folded into isPremium.
    canCsvExport:  isAdmin || isPremium,
    canPdfExport:  isAdmin || isPremium,
    canImport:     isAdmin || isPremium,
    canInvoiceOCR: isAdmin || isPremium,
    canBilling:    isBasic,
    canSubscription: isBasic,
    // Premium-locked features (Phase 8). Premium is "Coming Soon" until
    // PREMIUM_ENABLED=true is set — only admins bypass for now.
    canPremiumUI:              isAdmin || isPremium,
    canPremiumAnalytics:       isAdmin || isPremium,
    canPremiumDashboard:       isAdmin || isPremium,
    canUsePremiumTemplates:    isAdmin || isPremium,
    canUsePremiumFields:       isAdmin || isPremium,
    canUsePremiumFilters:      isAdmin || isPremium,
    canUsePremiumExports:      isAdmin || isPremium,
  };
}

// Format FastAPI error responses safely for JSX rendering. FastAPI 422
// returns { detail: [{msg,...},...] } which crashes React if rendered
// directly.
export function formatAuthError(err, fallback = "Something went wrong. Please try again.") {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : String(e))).join(" ") || fallback;
  }
  if (typeof detail?.msg === "string") return detail.msg;
  return String(detail);
}
